import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { nsConfirm } from '../shared/ns-confirm'
import {
  Settings,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  BarChart3,
  Users,
  Calendar,
  ShieldCheck
} from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'

const AdminTools = () => {
  const [updateResults, setUpdateResults] = useState(null)
  const [totpResults, setTotpResults] = useState(null)
  const queryClient = useQueryClient()

  const { data: quotaStatus, isLoading: loadingStatus, refetch } = useQuery(
    'admin-quota-status',
    () => apiService.get('/admin-tools/quota-status').then(res => res.data),
    { retry: 1 }
  )

  const updateQuotasMutation = useMutation(
    () => apiService.post('/admin-tools/update-client-quotas'),
    {
      onSuccess: (response) => {
        const data = response.data
        setUpdateResults(data)
        toast.success(`Updated ${data.summary.updated_clients} clients successfully!`)
        refetch()
        queryClient.invalidateQueries('client')
        queryClient.invalidateQueries('clients')
      },
      onError: (error) => {
        console.error('Update error:', error)
        toast.error(error.response?.data?.message || 'Failed to update quotas')
      }
    }
  )

  const handleUpdateQuotas = async () => {
    const ok = await nsConfirm({
      tone: 'warning',
      icon: 'users',
      title: 'Update all client quotas?',
      message: 'Every client’s storage and usage limits will be recalculated from their current subscription plan. This affects all clients.',
      confirmLabel: 'Update quotas',
    })
    if (ok) updateQuotasMutation.mutate()
  }

  const migrateTotpMutation = useMutation(
    () => apiService.post('/admin-tools/migrate-totp-secrets'),
    {
      onSuccess: (response) => {
        const data = response.data
        setTotpResults(data.summary)
        toast.success(`Encrypted ${data.summary.migrated} TOTP secret(s)`)
      },
      onError: (error) => {
        console.error('TOTP migration error:', error)
        toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to migrate TOTP secrets')
      }
    }
  )

  const handleMigrateTotp = async () => {
    const ok = await nsConfirm({
      tone: 'accent',
      icon: 'shield',
      title: 'Encrypt stored 2FA secrets?',
      message: 'Any plaintext TOTP (2FA) secrets will be encrypted at rest using the configured key. Already-encrypted secrets are skipped. This is safe to run more than once.',
      confirmLabel: 'Encrypt secrets',
    })
    if (ok) migrateTotpMutation.mutate()
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div>
        <h1 className="font-bold" style={{ fontSize: '1.75rem' }}>Admin Tools</h1>
        <p className="text-muted">System administration and maintenance tools</p>
      </div>

      {/* Quota Management */}
      <div className="card card-pad">
        <div className="flex items-center" style={{ gap: '0.5rem', marginBottom: '1rem' }}>
          <Settings style={{ width: '1.25rem', height: '1.25rem', color: 'var(--accent)' }} />
          <h2 className="font-semibold" style={{ fontSize: '1.125rem' }}>Client Quota Management</h2>
        </div>

        {loadingStatus ? (
          <LoadingSpinner size="sm" text="Loading quota status..." />
        ) : quotaStatus ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Summary Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
              <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--surface-2)', borderRadius: 'var(--radius)' }}>
                <Users style={{ width: '1.5rem', height: '1.5rem', color: 'var(--text-soft)', margin: '0 auto 0.25rem' }} />
                <div className="font-bold" style={{ fontSize: '1.5rem' }}>{quotaStatus.summary.total_clients}</div>
                <div className="text-muted" style={{ fontSize: '0.875rem' }}>Total Clients</div>
              </div>

              <div style={{ textAlign: 'center', padding: '1rem', background: 'rgba(245,158,11,0.08)', borderRadius: 'var(--radius)' }}>
                <AlertTriangle style={{ width: '1.5rem', height: '1.5rem', color: 'var(--warning)', margin: '0 auto 0.25rem' }} />
                <div className="font-bold" style={{ fontSize: '1.5rem', color: 'var(--warning)' }}>{quotaStatus.summary.needs_update}</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--warning)' }}>Need Updates</div>
              </div>

              <div style={{ textAlign: 'center', padding: '1rem', background: 'rgba(59,130,246,0.08)', borderRadius: 'var(--radius)' }}>
                <BarChart3 style={{ width: '1.5rem', height: '1.5rem', color: '#3b82f6', margin: '0 auto 0.25rem' }} />
                <div className="font-bold" style={{ fontSize: '1.5rem', color: '#3b82f6' }}>
                  {quotaStatus.summary.plans.basic}B / {quotaStatus.summary.plans.pro}P / {quotaStatus.summary.plans.enterprise}E
                </div>
                <div style={{ fontSize: '0.875rem', color: '#3b82f6' }}>Plans (B/P/E)</div>
              </div>

              <div style={{ textAlign: 'center', padding: '1rem', background: 'rgba(34,197,94,0.08)', borderRadius: 'var(--radius)' }}>
                <CheckCircle style={{ width: '1.5rem', height: '1.5rem', color: 'var(--success)', margin: '0 auto 0.25rem' }} />
                <div className="font-bold" style={{ fontSize: '1.5rem', color: 'var(--success)' }}>
                  {quotaStatus.summary.total_clients - quotaStatus.summary.needs_update}
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--success)' }}>Up to Date</div>
              </div>
            </div>

            {/* Action Button */}
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '1rem' }}>
              <button
                className="btn btn-primary flex items-center gap-2"
                onClick={handleUpdateQuotas}
                disabled={updateQuotasMutation.isLoading || quotaStatus.summary.needs_update === 0}
              >
                {updateQuotasMutation.isLoading
                  ? <RefreshCw style={{ width: '1rem', height: '1rem' }} />
                  : <Settings style={{ width: '1rem', height: '1rem' }} />
                }
                {quotaStatus.summary.needs_update === 0
                  ? 'All Quotas Up to Date'
                  : `Update ${quotaStatus.summary.needs_update} Client Quotas`
                }
              </button>
            </div>

            {/* Client List */}
            {quotaStatus.clients && (
              <div style={{ marginTop: '1.5rem' }}>
                <h3 className="font-medium" style={{ fontSize: '1.125rem', marginBottom: '1rem' }}>
                  Client Quota Status
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '24rem', overflowY: 'auto' }}>
                  {quotaStatus.clients.map((client) => (
                    <div
                      key={client.id}
                      className="flex items-center justify-between"
                      style={{
                        padding: '0.75rem',
                        borderRadius: 'var(--radius)',
                        border: `1px solid ${client.needs_update ? 'rgba(245,158,11,0.3)' : 'rgba(34,197,94,0.3)'}`,
                        background: client.needs_update ? 'rgba(245,158,11,0.05)' : 'rgba(34,197,94,0.05)'
                      }}
                    >
                      <div className="flex items-center" style={{ gap: '0.75rem' }}>
                        {client.needs_update
                          ? <AlertTriangle style={{ width: '1rem', height: '1rem', color: 'var(--warning)' }} />
                          : <CheckCircle style={{ width: '1rem', height: '1rem', color: 'var(--success)' }} />
                        }
                        <div>
                          <div className="font-medium">{client.company_name}</div>
                          <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                            {client.plan.charAt(0).toUpperCase() + client.plan.slice(1)} Plan
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div className="font-medium">{client.usage_display}</div>
                        {client.needs_update && (
                          <div style={{ fontSize: '0.875rem', color: 'var(--warning)' }}>
                            Should be: {client.used}/{client.expected_quota}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <AlertTriangle style={{ width: '3rem', height: '3rem', color: 'var(--text-faint)', margin: '0 auto 1rem' }} />
            <p className="text-muted">Failed to load quota status</p>
            <button className="btn btn-primary" onClick={() => refetch()} style={{ marginTop: '0.5rem' }}>
              Retry
            </button>
          </div>
        )}
      </div>

      {/* Security Maintenance */}
      <div className="card card-pad">
        <div className="flex items-center" style={{ gap: '0.5rem', marginBottom: '1rem' }}>
          <ShieldCheck style={{ width: '1.25rem', height: '1.25rem', color: 'var(--accent)' }} />
          <h2 className="font-semibold" style={{ fontSize: '1.125rem' }}>Security Maintenance</h2>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <div className="font-medium">Encrypt stored 2FA secrets</div>
            <div className="text-muted" style={{ fontSize: '0.875rem' }}>
              Encrypts any plaintext TOTP secrets from before encryption-at-rest was enabled.
              Already-encrypted secrets are skipped — safe to run repeatedly.
            </div>
          </div>

          {totpResults && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', textAlign: 'center' }}>
              <div>
                <div className="font-bold" style={{ fontSize: '1.5rem' }}>{totpResults.total_with_secret}</div>
                <div className="text-muted" style={{ fontSize: '0.875rem' }}>With Secret</div>
              </div>
              <div>
                <div className="font-bold" style={{ fontSize: '1.5rem', color: 'var(--success)' }}>{totpResults.migrated}</div>
                <div className="text-muted" style={{ fontSize: '0.875rem' }}>Encrypted</div>
              </div>
              <div>
                <div className="font-bold" style={{ fontSize: '1.5rem', color: 'var(--text-soft)' }}>{totpResults.alreadyEncrypted}</div>
                <div className="text-muted" style={{ fontSize: '0.875rem' }}>Already Done</div>
              </div>
              <div>
                <div className="font-bold" style={{ fontSize: '1.5rem', color: totpResults.failed ? 'var(--warning)' : 'var(--text-soft)' }}>{totpResults.failed}</div>
                <div className="text-muted" style={{ fontSize: '0.875rem' }}>Failed</div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              className="btn btn-primary flex items-center gap-2"
              onClick={handleMigrateTotp}
              disabled={migrateTotpMutation.isLoading}
            >
              {migrateTotpMutation.isLoading
                ? <RefreshCw style={{ width: '1rem', height: '1rem' }} />
                : <ShieldCheck style={{ width: '1rem', height: '1rem' }} />
              }
              {migrateTotpMutation.isLoading ? 'Encrypting…' : 'Encrypt 2FA Secrets'}
            </button>
          </div>
        </div>
      </div>

      {/* Update Results */}
      {updateResults && (
        <div className="card card-pad">
          <div className="flex items-center" style={{ gap: '0.5rem', marginBottom: '1rem' }}>
            <CheckCircle style={{ width: '1.25rem', height: '1.25rem', color: 'var(--success)' }} />
            <h2 className="font-semibold" style={{ fontSize: '1.125rem' }}>Update Results</h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '1rem', textAlign: 'center' }}>
              <div>
                <div className="font-bold" style={{ fontSize: '1.5rem' }}>{updateResults.summary.total_clients}</div>
                <div className="text-muted" style={{ fontSize: '0.875rem' }}>Total Clients</div>
              </div>
              <div>
                <div className="font-bold" style={{ fontSize: '1.5rem', color: 'var(--success)' }}>{updateResults.summary.updated_clients}</div>
                <div className="text-muted" style={{ fontSize: '0.875rem' }}>Updated</div>
              </div>
              <div>
                <div className="font-bold" style={{ fontSize: '1.5rem', color: 'var(--text-soft)' }}>{updateResults.summary.already_correct}</div>
                <div className="text-muted" style={{ fontSize: '0.875rem' }}>Already Correct</div>
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <button className="btn btn-ghost" onClick={() => setUpdateResults(null)}>
                Dismiss Results
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminTools
